/**
 * Startup session restore (thin wrapper over `runSessionRestore`).
 *
 * On mount: hydrate any persisted token, then — if it decodes to a usable user —
 * render the real app shell IMMEDIATELY from the JWT payload and validate the
 * session with `/auth/me` in the BACKGROUND. The decision layer lives in
 * `session-restore.logic.ts` (pure + unit-tested); this hook only injects the
 * real dependencies and instrumentation.
 *
 * The store starts in "checking" and this hook is what ALWAYS moves it out of
 * that state. It leaves "checking" as soon as the local token hydration + decode
 * finish (sub-100ms), NOT after the network round-trip — so the shell is up
 * almost immediately. Every data hook still gates on
 * `status === "authenticated"`, and the optimistic JWT user is a UI
 * convenience only: the API re-checks every permission and `/auth/me` overwrites
 * it. This hook never blocks the first paint.
 */

import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { hydrateTokens, clearTokens } from "@/lib/auth-token";
import { decodeUserFromAccessToken } from "@/lib/auth-jwt";
import { clearPrivateCaches, queryKeys } from "@/lib/query-client";
import { measureStartup, timeStartup } from "@/lib/startup-perf";
import { authApi, toCurrentUser } from "./auth.api";
import { useAuthStore } from "./auth.store";
import { beginSessionValidation, endSessionValidation } from "./restore-flag";
import { runSessionRestore } from "./session-restore.logic";

export function useSessionRestore(): void {
  const qc = useQueryClient();
  const setAuthenticated = useAuthStore((s) => s.setAuthenticated);
  const setAnonymous = useAuthStore((s) => s.setAnonymous);

  useEffect(() => {
    let active = true;
    void runSessionRestore({
      hydrate: () => timeStartup("token-hydration", () => hydrateTokens()),
      decodeUser: decodeUserFromAccessToken,
      // Goes through the api-client, so an expired access token is refreshed +
      // retried transparently before this ever throws. `/auth/refresh` timing is
      // recorded inside `auth-refresh.ts`.
      fetchCurrentUser: () => timeStartup("auth-me", async () => toCurrentUser(await authApi.me())),
      clearTokens,
      clearPrivateCaches: () => clearPrivateCaches(qc),
      seedUser: (user) => qc.setQueryData(queryKeys.currentUser, user),
      setAuthenticated,
      setAnonymous,
      beginValidation: beginSessionValidation,
      endValidation: endSessionValidation,
      isActive: () => active,
      // Dev-only: "time to first shell" (from navigation start) + a reason word.
      // No token, header, email or any personal data is ever logged.
      mark: (reason) => measureStartup("authenticated-shell", reason),
    });
    return () => {
      active = false;
    };
  }, [qc, setAuthenticated, setAnonymous]);
}
